package com.neurobs.ccabserver.subjectsessionview.components.sessionview

import com.neurobs.ccab.{Run, RunMatch, SessionDisplayData, SessionLogStorage}
import com.neurobs.ccabserver.subjectsessionview.states.{RunSelectionStateGenerator, SessionSelectionStateGenerator, UserState}
import com.neurobs.js.util.StringUtilities.dateTimeString
import com.neurobs.react.{ReactElementFactory, StyleUtil}
import com.neurobs.react.semanticui.{UiContainerFactory, UiLoaderFactory, UiTableFactory}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useEffect
import slinky.core.facade.ReactElement
import slinky.web.html._

import scala.scalajs.js

@react
object SessionTable {
  case class Props()
  val component = FunctionalComponent[Props] { props =>

    val sessionSelectionState = SessionSelectionStateGenerator.accessState

    def sortRuns(runs: List[Run]) = runs.sortBy(run => {
      val num = run.startTime.map(_.instant.getEpochSecond.toInt*(-1)).getOrElse(0)
      num
    }).reverse

    def runs(session: SessionDisplayData): List[Run] = session.testRuns.map(testRun => Run(None, Some(testRun))).toList
    def matchedRuns(sessionLog: SessionLogStorage, sessionDisplayData: SessionDisplayData): List[Run] = {
      sessionLog.state.test_log match {
        case None => sortRuns(runs(sessionDisplayData))
        case Some(logTestRuns) =>
          val matchedRuns = new RunMatch(logTestRuns, sessionLog.session.group.tests, sessionDisplayData.testRuns.toList).getRuns
          sortRuns(matchedRuns)
      }
    }

    sessionSelectionState.selectedSessionState.value match {
      case None => null
      case Some(session) =>
        sessionSelectionState.sessionLogCache.searchCache(session.session.session.sessionId) match {
          case None =>
            UiContainerFactory.Segment(
              UiLoaderFactory.ActiveTextLoader("Loading tests...")("mini")
            )(("centered raised", Seq[(String, js.Any)](("height", "100px"))))
          case Some(sessionLogEither) =>
						val defaultTestRunId = sessionSelectionState.defaultTestRunId
						sessionSelectionState.defaultTestRunId = None
						sessionLogEither match {
							case Left(error) => SessionTableView(runs(session), defaultTestRunId)
							case Right(sessionLog) => SessionTableView(matchedRuns(sessionLog, session), defaultTestRunId)
						}
        }
    }
  }
}


@react
object SessionTableView {
  case class Props(runs: List[Run], defaultTestRunId: Option[Long])
  val component = FunctionalComponent[Props] { props =>

    def runs = props.runs

    val userState = UserState.accessState
    val runSelectionState = RunSelectionStateGenerator.accessState

		useEffect(() => {
			props.defaultTestRunId.foreach { id =>
				runs.find(_.testRun.exists(_.testRun.id == id)).foreach(r => runSelectionState.selectedRunState.setValue(Some(r)))
			}
		}, Vector(props.defaultTestRunId))

    def isSiteAdmin = userState.userData.isSiteAdmin

    def selectRun(run: Run) = {
      runSelectionState.selectedRunState.setValue(Some(run))
    }

    val tableColumns = Seq("Time", "Test")
    val siteAdminTableColumns = Seq("DID", "Time", "Test")

    def TableHeader = {
      val headerColumns = if (isSiteAdmin) siteAdminTableColumns else tableColumns
      ReactElementFactory.TableHead(
        ReactElementFactory.TableRow(
          headerColumns.map(ReactElementFactory.Th(_))
        )
      )
    }

    def TableBody = ReactElementFactory.TableBody(
      runs.filterNot(_.testRun.map(_.testName == Some("Progress Update")).getOrElse(false)).map(run => {
        if (isSiteAdmin) SiteAdminTableBodyRow(run)
        else TableBodyRow(run)
      })
    )

    def startTime(run: Run) = run.startTime.map(dateTimeString(_)).getOrElse("")
    def testName(run: Run) = run.testName.getOrElse("Unmatched test in session log")
    def databaseId(run: Run) = run.testRun.map(_.testRun.id.toString).getOrElse("")
    def hasError(run: Run) = run.testRun.flatMap(_.testRun.runError).isDefined
    def isUnmatched(run: Run) = run.logTestRun.isDefined && run.testRun.isEmpty

    def rowClass(run: Run) = {
      if (isSiteAdmin) {
        if (hasError(run)) "error" else if (isUnmatched(run)) "warning" else ""
      } else {
        if (hasError(run) || isUnmatched(run)) "error" else ""
      }
    }

    def TableBodyRow(run: Run) = {

      val rowValues: List[ReactElement] = List(startTime(run), testName(run))
      val cellStyle = Seq[(String, js.Any)](("fontSize", "11px"))

      def handleSelect = () => selectRun(run)

      ReactElementFactory.SelectableTableRow(handleSelect)(
        rowValues.map(ReactElementFactory.Td(_)(cellStyle))
      )(rowClass(run))
    }

    def SiteAdminTableBodyRow(run: Run) = {

      val rowValues: List[ReactElement] = List(databaseId(run), startTime(run), testName(run) + (if (isUnmatched(run)) " (session log only)" else ""))
      val cellStyle = Seq[(String, js.Any)](("fontSize", "11px"))

      def handleSelect = () => selectRun(run)

      ReactElementFactory.SelectableTableRow(handleSelect)(
        rowValues.map(ReactElementFactory.Td(_)(cellStyle))
      )(rowClass(run))
    }

    def segmentStyle = Seq[(String, js.Any)](("maxHeight", "250px"), ("overflowY", "auto"))

    div(
      className := "ui fluid basic segment",
      style := StyleUtil.makeStyle(segmentStyle:_*),
    )(
      UiTableFactory.Table(
        TableHeader,
        TableBody
      )("celled blue single line selectable")
    )

  }
}
